import type { ToolUseItem } from '../../../../contracts/harness.types';
import { isRecord } from '../../../../contracts/parse-utils';
import { readToolResultJsonCandidates } from '../../../../contracts/toolResult';
import {
  CREATE_ROUTINE_TOOL_NAMES,
  ROUTINE_TOOL_NAMES,
  routineToolNameSet,
} from '../../../../contracts/workflows/lib/routineToolNames';
import type { ObjectCreationKind } from './object-creation.types';

export type WrittenObjectKind =
  | 'patient'
  | 'session'
  | 'task'
  | 'document'
  | 'template'
  | 'routine';

export type ObjectWriteAction = 'create' | 'update';

export interface WrittenObject {
  kind: WrittenObjectKind;
  id: string;
  sessionId?: string;
  fallbackTitle?: string;
}

type WrittenRef = Omit<WrittenObject, 'kind'>;

type ReadWrittenRefs = (item: ToolUseItem) => WrittenRef[];

interface CardTitleKeys {
  inputTitleKeys: readonly string[];
  resultTitleKeys: readonly string[];
}

interface ObjectWriteSpec {
  kind: WrittenObjectKind;
  action: ObjectWriteAction;
  read: ReadWrittenRefs;
  card?: CardTitleKeys;
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value.trim() : undefined;

const fromResult = (item: ToolUseItem, ...keys: string[]): string | undefined => {
  for (const candidate of readToolResultJsonCandidates(item)) {
    for (const key of keys) {
      const value = text(candidate[key]);
      if (value) return value;
    }
  }
  return undefined;
};

const fromInput = (item: ToolUseItem, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = text(item.input?.[key]);
    if (value) return value;
  }
  return undefined;
};

const one = (id: string | undefined, fallbackTitle?: string): WrittenRef[] =>
  id ? [{ id, ...(fallbackTitle ? { fallbackTitle } : {}) }] : [];

const readTaskRecords = (item: ToolUseItem, key: string): WrittenRef[] => {
  for (const candidate of readToolResultJsonCandidates(item)) {
    const entries = candidate[key];
    if (!Array.isArray(entries)) continue;
    return entries.flatMap((entry) =>
      isRecord(entry) ? one(text(entry.id), text(entry.title)) : one(text(entry))
    );
  }
  return [];
};

const SESSION_TITLE_KEYS = ['title', 'session_title', 'name'];
const PATIENT_TITLE_KEYS = ['name', 'full_name', 'patient_name'];
const TASK_TITLE_KEYS = ['title', 'task_title', 'name'];
const TEMPLATE_TITLE_KEYS = ['name', 'template_name', 'title'];

const DEFAULT_TITLE_KEYS: Record<ObjectCreationKind, readonly string[]> = {
  session: SESSION_TITLE_KEYS,
  patient: PATIENT_TITLE_KEYS,
  document: ['title', 'document_title', 'name'],
  task: TASK_TITLE_KEYS,
  template: TEMPLATE_TITLE_KEYS,
};

const cardTitleKeys = (
  inputTitleKeys: readonly string[],
  resultTitleKeys: readonly string[] = inputTitleKeys
): CardTitleKeys => ({ inputTitleKeys, resultTitleKeys });

/**
 * Title from a structured record using the given keys, if any.
 *
 * NOTE: this MAY return PHI (patient name / full_name). It is for
 * authenticated in-app display only — never route it into logs, analytics
 * events, or URLs.
 */
export const titleFromRecord = (
  record: Record<string, unknown> | undefined,
  keys: readonly string[]
): string | undefined => {
  if (!record) return undefined;
  for (const key of keys) {
    const value = text(record[key]);
    if (value) return value;
  }
  return undefined;
};

export const inputTitleKeysFor = (
  toolName: string,
  kind: ObjectCreationKind
): readonly string[] =>
  WRITE_SPECS[toolName]?.card?.inputTitleKeys ?? DEFAULT_TITLE_KEYS[kind];

export const resultTitleKeysFor = (
  toolName: string,
  kind: ObjectCreationKind
): readonly string[] =>
  WRITE_SPECS[toolName]?.card?.resultTitleKeys ?? DEFAULT_TITLE_KEYS[kind];

/**
 * Title carried directly on the create tool's own input, if any. Returns
 * `undefined` when the create call didn't name the object — e.g. a
 * `create_session` whose title is set later by `set_session_title`. The
 * caller resolves that (and the untitled fallback) via `resolveCreatedTitle`.
 *
 * NOTE: this MAY return PHI (patient name / full_name / first + last). It is
 * for authenticated in-app display only — never route it into logs, analytics
 * events, or URLs.
 */
export const directObjectTitle = (
  item: ToolUseItem,
  kind: ObjectCreationKind
): string | undefined => {
  const input = item.input ?? {};
  const fromKeys = titleFromRecord(input, inputTitleKeysFor(item.name, kind));
  if (fromKeys) return fromKeys;
  if (kind === 'patient') {
    const composed = [text(input.first_name), text(input.last_name)]
      .filter(Boolean)
      .join(' ');
    if (composed) return composed;
  }
  // A create_clinical_task call may carry its one task inside `tasks[]`.
  // That shape is also used by its approval preview, so use the first title
  // for the batch receipt rather than showing an unhelpful untitled row.
  if (kind === 'task' && Array.isArray(input.tasks)) {
    const first: unknown = input.tasks[0];
    if (first && typeof first === 'object') {
      const title = text((first as Record<string, unknown>).title);
      if (title) return title;
    }
  }
  return undefined;
};

const patientCreate: ReadWrittenRefs = (item) =>
  one(
    fromResult(item, 'patient_profile_id', 'patient_id'),
    directObjectTitle(item, 'patient')
  );

const patientUpdate: ReadWrittenRefs = (item) =>
  one(
    fromResult(item, 'patient_profile_id', 'patient_id') ??
      fromInput(item, 'patient_profile_id', 'patient_id')
  );

const sessionCreate: ReadWrittenRefs = (item) =>
  one(fromResult(item, 'session_id'), directObjectTitle(item, 'session'));

const sessionUpdate: ReadWrittenRefs = (item) =>
  one(
    fromInput(item, 'session_id') ?? fromResult(item, 'session_id'),
    item.name === 'set_session_title'
      ? (fromInput(item, 'title') ?? fromResult(item, 'title'))
      : undefined
  );

const taskCreate: ReadWrittenRefs = (item) => readTaskRecords(item, 'tasks');

const taskUpdate: ReadWrittenRefs = (item) =>
  one(fromResult(item, 'id') ?? fromInput(item, 'task_id'), fromResult(item, 'title'));

const documentWrite =
  (titleKeys: readonly string[]): ReadWrittenRefs =>
  (item) => {
    const id = fromResult(item, 'document_id') ?? fromInput(item, 'document_id');
    const sessionId = fromInput(item, 'session_id') ?? fromResult(item, 'session_id');
    if (!id || !sessionId) return [];
    const fallbackTitle =
      fromInput(item, ...titleKeys) ??
      fromResult(item, 'name', 'title', 'document_title') ??
      directObjectTitle(item, 'document');
    return [{ id, sessionId, ...(fallbackTitle ? { fallbackTitle } : {}) }];
  };

const documentCreate = documentWrite([]);

const templateWrite: ReadWrittenRefs = (item) =>
  one(
    fromResult(item, 'template_id') ?? fromInput(item, 'template_id'),
    fromResult(item, 'name', 'template_name') ??
      fromInput(item, 'name', 'template_name', 'title')
  );

const routineWrite: ReadWrittenRefs = (item) =>
  one(
    fromResult(item, 'workflow_id') ?? fromInput(item, 'workflow_id'),
    fromResult(item, 'name') ?? fromInput(item, 'name')
  );

// A row with title keys is a create whose single object also gets its own card.
const WRITE_TOOLS: ReadonlyArray<
  readonly [
    readonly string[],
    WrittenObjectKind,
    ObjectWriteAction,
    ReadWrittenRefs,
    CardTitleKeys?,
  ]
> = [
  [
    ['create_patient_profile'],
    'patient',
    'create',
    patientCreate,
    cardTitleKeys(PATIENT_TITLE_KEYS),
  ],
  [['update_patient_context'], 'patient', 'update', patientUpdate],
  [
    ['create_session', 'create_session_for_member'],
    'session',
    'create',
    sessionCreate,
    cardTitleKeys(SESSION_TITLE_KEYS),
  ],
  [
    ['create_session_from_ehr_appointment', 'ensure_session_for_ehr_appointment'],
    'session',
    'create',
    sessionCreate,
  ],
  [
    [
      'set_session_title',
      'set_session_status',
      'update_session_time',
      'link_session_to_patient',
      'link_session_to_appointment',
    ],
    'session',
    'update',
    sessionUpdate,
  ],
  [
    ['create_clinical_task'],
    'task',
    'create',
    taskCreate,
    cardTitleKeys(TASK_TITLE_KEYS),
  ],
  [['generate_session_clinical_tasks'], 'task', 'create', taskCreate],
  [
    [
      'update_task_status',
      'assign_clinical_task',
      'unassign_clinical_task',
      'link_clinical_task',
      'unlink_clinical_task',
    ],
    'task',
    'update',
    taskUpdate,
  ],
  [
    ['bulk_update_clinical_task_status'],
    'task',
    'update',
    (item) => readTaskRecords(item, 'updated'),
  ],
  [
    ['create_session_document'],
    'document',
    'create',
    documentCreate,
    cardTitleKeys(
      ['title', 'document_title', 'name', 'document_name'],
      ['name', 'title', 'document_title']
    ),
  ],
  [
    ['generate_note_from_template'],
    'document',
    'create',
    documentCreate,
    cardTitleKeys(
      ['title', 'template_name'],
      ['name', 'title', 'document_title', 'template_name']
    ),
  ],
  [
    ['generate_prechart_document'],
    'document',
    'create',
    documentCreate,
    cardTitleKeys(['name', 'title'], ['name', 'title', 'document_title']),
  ],
  [
    ['rename_session_document', 'edit_session_document'],
    'document',
    'update',
    documentWrite(['name', 'title', 'document_title', 'new_name', 'new_title']),
  ],
  [
    ['create_template'],
    'template',
    'create',
    templateWrite,
    cardTitleKeys(TEMPLATE_TITLE_KEYS),
  ],
  [['update_template', 'edit_template_section'], 'template', 'update', templateWrite],
  [[...CREATE_ROUTINE_TOOL_NAMES], 'routine', 'create', routineWrite],
  [
    [
      ...routineToolNameSet(
        ROUTINE_TOOL_NAMES.update,
        ROUTINE_TOOL_NAMES.schedule,
        ROUTINE_TOOL_NAMES.setEventTriggers
      ),
    ],
    'routine',
    'update',
    routineWrite,
  ],
];

const WRITE_SPECS: Readonly<Record<string, ObjectWriteSpec>> = Object.fromEntries(
  WRITE_TOOLS.flatMap(([names, kind, action, read, card]) =>
    names.map((name) => [name, { kind, action, read, card }] as const)
  )
);

/** The object-card tools, for the presentation registry to compose. */
export const OBJECT_TOOL_NAMES: readonly string[] = Object.keys(WRITE_SPECS).filter(
  (name) => WRITE_SPECS[name]?.card !== undefined
);

export const isObjectCreationToolName = (name: string): boolean =>
  WRITE_SPECS[name]?.card !== undefined;

export const objectKindForTool = (name: string): ObjectCreationKind | undefined => {
  const spec = WRITE_SPECS[name];
  return spec?.card && spec.kind !== 'routine' ? spec.kind : undefined;
};

export const objectWriteFor = (
  toolName: string
): { kind: WrittenObjectKind; action: ObjectWriteAction } | undefined => {
  const spec = WRITE_SPECS[toolName];
  return spec ? { kind: spec.kind, action: spec.action } : undefined;
};

/** NOTE: `fallbackTitle` may carry PHI — render-only, never log or serialise. */
export const writtenObjects = (items: readonly ToolUseItem[]): WrittenObject[] => {
  const seen = new Set<string>();
  return items.flatMap((item) => {
    const spec = WRITE_SPECS[item.name];
    if (!spec || item.status !== 'complete' || item.result?.is_error) return [];
    return spec.read(item).flatMap((ref) => {
      const key = `${spec.kind}:${ref.id}`;
      if (seen.has(key)) return [];
      seen.add(key);
      return [{ kind: spec.kind, ...ref }];
    });
  });
};
